import { Button, TableItem } from 'components/atoms';
import { Table } from 'components/organisms';
import { useFormikContext } from 'formik';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiNavigationArrow } from 'react-icons/pi';
import { ILabelReportDetailGroupedItem } from 'services/label-report/types';
import { formatCurrency } from 'utils/global/currency.utils';
import { ILabelReportFormValues } from '../LabelReportForm';

const goToChannelVideo = (channelId: string, videoId: string) =>
  window.open(
    `/channel/${channelId}/channel-video/${videoId}/player`,
    '_blank',
    'noopener,noreferrer'
  );

enum SORT_COLUMNS {
  title,
  amountEarned,
  amountLabelEarned,
  ean,
  isrc,
  channelId,
  videoId,
  salesChannelCount,
  actions,
}

interface TableNode extends ILabelReportDetailGroupedItem {
  id: string;
  salesChannelCount: number;
}

interface LabelReportGroupedItemTableProps {
  dataKey: keyof Pick<
    ILabelReportFormValues,
    'groupedProducts' | 'groupedChannels'
  >;
  tableId: string;
}

const hasChannelVideoColumns = (dataKey: string) =>
  dataKey !== 'groupedProducts' && dataKey !== 'groupedChannels';

const LabelReportGroupedItemTable: React.FC<
  LabelReportGroupedItemTableProps
> = ({ dataKey, tableId }) => {
  const { values } = useFormikContext<ILabelReportFormValues>();
  const { t } = useTranslation();
  const columnsVisibility = useTableColumnsVisibility(tableId, SORT_COLUMNS);
  const showChannelVideo = hasChannelVideoColumns(dataKey);

  const items: ILabelReportDetailGroupedItem[] = values?.[dataKey] ?? [];
  const tableData: TableNode[] = items.map((row, index) => ({
    ...row,
    id: `${dataKey}-${index}`,
    salesChannelCount: row.salesChannelRows?.length ?? 0,
  }));

  const columns = [
    {
      label: t('input.label.title'),
      renderCell: (item: TableNode) => (
        <TableItem>
          <div className='flex flex-col gap-0.5'>
            <TableItem.Text text={item.title} />
            {item.secondTitle && (
              <TableItem.Text text={item.secondTitle} className='text-sm text-gray-500' />
            )}
          </div>
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.title),
    },
    {
      label: t('input.label.amountEarned'),
      renderCell: (item: TableNode) => (
        <TableItem>
          <TableItem.Currency text={formatCurrency(item.amountEarned)} />
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.amountEarned),
    },
    {
      label: t('input.label.amountLabelEarned'),
      renderCell: (item: TableNode) => (
        <TableItem>
          <TableItem.Currency text={formatCurrency(item.amountLabelEarned)} />
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.amountLabelEarned),
    },
    {
      label: t('input.label.ean'),
      renderCell: (item: TableNode) => (
        <TableItem>
          <TableItem.Text text={item.ean} />
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.ean),
    },
    {
      label: t('input.label.isrc'),
      renderCell: (item: TableNode) => (
        <TableItem>
          <TableItem.Text text={item.isrc} />
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.isrc),
    },
    ...(showChannelVideo
      ? [
          {
            label: t('input.label.channelId'),
            renderCell: (item: TableNode) => (
              <TableItem>
                <TableItem.Text text={item.channelId} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.channelId),
          },
          {
            label: t('input.label.videoId'),
            renderCell: (item: TableNode) => (
              <TableItem>
                <TableItem.Text text={item.videoId} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.videoId),
          },
        ]
      : []),
    {
      label: t('input.label.salesChannelsCount'),
      renderCell: (item: TableNode) => (
        <TableItem>
          <TableItem.Text text={String(item.salesChannelCount)} />
        </TableItem>
      ),
      resize: true,
      hide: columnsVisibility.hide(SORT_COLUMNS.salesChannelCount),
    },
    ...(showChannelVideo
      ? [
          {
            label: '',
            renderCell: (item: TableNode) => (
              <TableItem justifyPosition='end'>
                {item.channelId && item.videoId && (
                  <Button
                    isClear
                    onlyIcon
                    onClick={() =>
                      goToChannelVideo(item.channelId, item.videoId)
                    }>
                                        <Button.Description description={t('input.label.video')} />

                                        <Button.Icon
                      icon={
                        <PiNavigationArrow transform='scale(-1,1)' />
                      }
                    />
                  </Button>
                )}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.actions),
          },
        ]
      : []),
  ];

  return (
    <Table
      id={tableId}
      data={{ nodes: tableData }}
      columns={columns}
    />
  );
};

export default LabelReportGroupedItemTable;
